<template>
  <b-modal
    :id="'add-version-modal-' + documentId"
    :modal-class="'has-confirmation-modal'"
    no-close-on-backdrop
    hide-footer
    :title="$t('message.manageDocuments.version.add')">
    <div
      :class="drag ? 'drag-zone' : ''"
      @drop.prevent="addFile"
      @dragover="drag = true"
      @dragleave.prevent>
      <div
        class="dragover-mask"
        @dragleave="drag=false" />
      <!--suppress XmlInvalidId -->
      <label for="file">{{ $t('message.manageDocuments.version.new') }}</label>
      <coozzy-form-file-input
        id="file"
        ref="file-input"
        :disabled="uploading"
        @change="addFile" />
      <div
        class="w-100 float-right mt-3">
        <coozzy-button
          size="normal"
          design="green"
          class="float-right ml-3"
          :disabled="uploading"
          @click="uploadClicked">
          {{ $t('message.generic.form.upload') }}
        </coozzy-button>
        <coozzy-form-checkbox
          v-if="!noAddAnother"
          v-model="addAnotherCheckbox"
          class="float-right pr-3 mt-2 mr-0 display-none-xs font-weight-lighter">
          {{ $t('message.generic.uploadMore') }}
        </coozzy-form-checkbox>
        <coozzy-button
          size="normal"
          :disabled="uploading"
          @click="$bvModal.hide(`add-version-modal-${documentId}`)">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import MediaApi from '../../misc/apis/MediaApi'
import Vue from 'vue'
import { media } from '@/mixins/media'
import CoozzyFormFileInput from '@/framework/components/form/fileInput/CoozzyFormFileInput.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'

export default {
  name: 'AddVersionModal',
  components: { CoozzyFormCheckbox, CoozzyButton, CoozzyFormFileInput },
  mixins: [media],
  props: {
    documentId: {
      type: String,
      default: ''
    },
    ownerId: {
      type: [String, Number],
      default: 0
    },
    noAddAnother: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      file: null,
      drag: false,
      addAnotherCheckbox: false,
      uploading: false,
      modalHiddenHandler: null,
      modalShowHandler: null,
      documentDropHandler: null,
      documentDragoverHandler: null
    }
  },
  mounted() {
    this.documentDropHandler = (e) => {
      e.preventDefault()
    }
    this.documentDragoverHandler = (e) => {
      e.preventDefault()
    }
    this.modalHiddenHandler = (bvEvent, modalId) => {
      if (modalId === 'add-version-modal-' + this.documentId) {
        this.removeDocumentDragListeners()
        this.file = null
      }
    }
    this.modalShowHandler = (bvEvent, modalId) => {
      if (modalId === 'add-version-modal-' + this.documentId) {
        this.addDocumentDragListeners()
      }
    }
    this.$root.$on('bv::modal::hidden', this.modalHiddenHandler)
    this.$root.$on('bv::modal::show', this.modalShowHandler)
  },
  beforeDestroy() {
    if (this.modalHiddenHandler) {
      this.$root.$off('bv::modal::hidden', this.modalHiddenHandler)
    }
    if (this.modalShowHandler) {
      this.$root.$off('bv::modal::show', this.modalShowHandler)
    }
    this.removeDocumentDragListeners()
  },
  methods: {
    addDocumentDragListeners() {
      this.removeDocumentDragListeners()
      document.addEventListener('drop', this.documentDropHandler)
      document.addEventListener('dragover', this.documentDragoverHandler)
    },
    removeDocumentDragListeners() {
      if (this.documentDropHandler) {
        document.removeEventListener('drop', this.documentDropHandler)
      }
      if (this.documentDragoverHandler) {
        document.removeEventListener('dragover', this.documentDragoverHandler)
      }
    },
    async uploadClicked() {
      this.uploading = true
      this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
      const selectedFile = this.file ? [this.file] : []
      const response = await this.uploadMedia(selectedFile, this.ownerId || '')
      if (response?.length > 0) {
        const media = response[0]
        MediaApi.addVersion(this.documentId, media.id)
          .then((document) => {
            this.$emit('version-added', document, this.addAnotherCheckbox)
            this.resetAndHide(this.addAnotherCheckbox === false)
          })
          .catch(e => {
            this.handleError(e)
          })
        this.uploading = false
      } else {
        this.uploading = false
      }
    },
    handleError(e) {
      this.resetAndHide(this.addAnotherCheckbox === false)
      console.log(e)
      if (e.response && e.response.data && e.response.data.message === 'Not supported MIME type') {
        Vue.toasted.show(this.$t('message.savingErrors.fileNotSupported'), { type: 'error' })
      } else {
        Vue.toasted.show(this.$t('message.savingErrors.file'), { type: 'error' })
      }
    },
    resetAndHide(hide) {
      this.uploading = false
      this.file = null
      if (this.$refs['file-input']) this.$refs['file-input'].resetPlaceholder()
      if (hide) {
        this.$bvModal.hide(`add-version-modal-${this.documentId}`)
      }
    },
    async addFile(event) {
      const files = await this.getFilesFromEvent(event)
      if (files && files.length > 0) {
        this.file = files[0]
        this.updateUploadedFileName(this, files[0].name)
      } else {
        if (this.$refs['file-input']) {
          this.$refs['file-input'].resetPlaceholder()
        }
      }
      this.drag = false
    }
  }
}
</script>

<style lang="scss" scoped>
  label {
    margin-bottom: 0;
  }
  .drag-zone  .dragover-mask{
    display: block !important;
    transition: z-index 0s step-end;
    z-index: 2500;;
  }
  .dragover-mask{
    display: none;
    background: rgba(255,255,255,0.3);
    border: 3px dashed $color-primary;
    border-radius: 5px;
    bottom: 0;
    left: 0;
    margin: 2px;
    position: absolute;
    right: 0;
    top: 0;
    transition: z-index .2s step-end;
    z-index: -1;
  }
</style>
